import React, { Fragment, useEffect, useState } from 'react'
import { Button, LoadingSpinner, DutyCard, DutyFormModal, ConfirmationModal } from '../components'
import {
    API_BASE_URL, DutyCardMeta, LoadingSpinnerMeta,
    DutyFormModalMeta, UpdateDutyFormMeta, ConfirmationModalMeta, DeleteDutyMeta
} from '../data'
import { Duty, DutyMetaProps } from '../data/types'
import { PlusIcon, ChevronLeftIcon } from '../icons'
import { Link } from 'react-router-dom'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: DutyMetaProps,
}

export const Duties = (props: Props) => {
    const { meta } = props
    const { canEditDuty, canDeleteDuty, canCreateDuty } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [duties, setDuties] = useState<Duty[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isCreateDutyModalOpen, setIsCreateDutyModalOpen] = useState(false)
    const [selectedDuty, setSelectedDuty] = useState<Duty | null>(null)
    const [isEditDutyModalOpen, setIsEditDutyModalOpen] = useState(false)
    const [isDeleteDutyModalOpen, setIsDeleteDutyModalOpen] = useState(false)
    const [isDeletingDuty, setIsDeletingDuty] = useState(false)
    const [filterText, setFilterText] = useState<string | null>(null)


    useEffect(() => {
        const fetchDuties = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/duty`, { credentials: 'include' })
                if (!response.ok) {
                    const text = await response.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const result = await response.json()
                setDuties(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching duties')
            }
            finally {
                setLoading(false)
            }
        }

        fetchDuties()

    }, [])

    const handleCreateDuty = async (dutyData: any) => {
        const response = await fetch(`${API_BASE_URL}/duty`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(dutyData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setDuties(prevDuties => [...prevDuties, result.model])
        }
    }

    const handleUpdateDuty = async (dutyData: { name: string, description?: string }) => {
        if (!selectedDuty) return

        const response = await fetch(`${API_BASE_URL}/duty/${selectedDuty.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(dutyData)
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedDuty = result.model
            setDuties(prev =>
                prev.map(duty =>
                    duty.id === updatedDuty.id ? updatedDuty : duty
                )
            )
        }

    }

    const handleDeleteDutyConfirm = async () => {
        if (!selectedDuty) return

        setIsDeletingDuty(true)
        try {
            const response = await fetch(`${API_BASE_URL}/duty/${selectedDuty.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setDuties(prev => prev.filter(duty => duty.id !== selectedDuty.id));
            setIsDeleteDutyModalOpen(false)
            setSelectedDuty(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Duty')
            setIsDeleteDutyModalOpen(false)
        } finally {
            setIsDeletingDuty(false)
        }
    }

    const handleEditDutyClick = (duty: Duty) => {
        setSelectedDuty(duty)
        setIsEditDutyModalOpen(true)
    }

    const handleDeleteDutyClick = (duty: Duty) => {
        setSelectedDuty(duty)
        setIsDeleteDutyModalOpen(true)
    }

    const handleDeleteDutyCancel = () => {
        setIsDeleteDutyModalOpen(false)
        setSelectedDuty(null)
    }

    const filterDuties = duties.filter((duty) => {
        if (filterText === null)
            return true
        return duty.name.toLowerCase().includes(filterText.toLowerCase())
    })



    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                        {canCreateDuty() && <div className="flex justify-center">
                            <Button onClick={() => setIsCreateDutyModalOpen(true)} size="lg">
                                <PlusIcon className="w-5 h-5" />
                                {meta.createButton}
                            </Button>
                        </div>}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && duties.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noDutyMessage}</p>
                        </div>
                    )}

                    {!loading && !error && duties.length > 0 && (
                        <>
                            <div className="mb-8 flex justify-center">
                                <input
                                    type="text"
                                    placeholder={meta.dutySearchPlaceHolder}
                                    value={filterText || ''}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                            </div>
                            {filterDuties.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filterDuties.map((duty) => (
                                        <DutyCard key={duty.id} duty={duty} meta={DutyCardMeta}
                                            onDelete={canDeleteDuty(duty) ? handleDeleteDutyClick : undefined} onEdit={canEditDuty(duty) ? handleEditDutyClick : undefined} />
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                                        <p className="text-xl">{meta.noDutyMessage}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>

            <DutyFormModal
                meta={DutyFormModalMeta}
                isOpen={isCreateDutyModalOpen}
                onClose={() => {
                    setIsCreateDutyModalOpen(false)
                    setSelectedDuty(null)
                }}
                onSubmit={handleCreateDuty}
            />

            <DutyFormModal
                meta={{ ...DutyFormModalMeta, ...UpdateDutyFormMeta }}
                isOpen={isEditDutyModalOpen}
                onClose={() => {
                    setIsEditDutyModalOpen(false)
                    setSelectedDuty(null)
                }}
                onSubmit={handleUpdateDuty}
                initialDuty={selectedDuty}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteDutyModalOpen}
                onConfirm={handleDeleteDutyConfirm}
                onCancel={handleDeleteDutyCancel}
                title={DeleteDutyMeta.confirmTitle}
                message={DeleteDutyMeta.confirmMessage}
                isLoading={isDeletingDuty}
            />


        </Fragment>
    )
}
